import "./ControlValveControlPanel.css";
import { useState, useEffect } from "react";

function ControlValveControlPanel({
  tag,
  data,
  update,
  close,
  defaultPosition
}) {

  // =========================
  // POSITIONING
  // =========================
  const [position, setPosition] = useState(
    defaultPosition || { x: 400, y: 120 }
  );

  const [dragging, setDragging] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  const handleMouseDown = (e) => {
    setDragging(true);
    setOffset({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  useEffect(() => {
    const move = (e) => {
      if (!dragging) return;

      setPosition({
        x: e.clientX - offset.x,
        y: e.clientY - offset.y
      });
    };

    const up = () => setDragging(false);

    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);

    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
  }, [dragging, offset]);

  // =========================
  // CONSTANTS
  // =========================
  const MAX_FLOW = 60;

  // =========================
  // PV MODEL
  // =========================
  const [pv, setPv] = useState(Number(data.pv ?? 0));

  useEffect(() => {

    const interval = setInterval(() => {

      const opening = Number(data.opening ?? 0);

      const flow = (opening / 100) * MAX_FLOW;

      const noise = (Math.random() - 0.5) * 0.6;
      const wave = Math.sin(Date.now() / 3000) * 0.4;

      const rawPV = flow + noise + wave;

      setPv(prev => prev + (rawPV - prev) * 0.08);

    }, 200);

    return () => clearInterval(interval);

  }, [data.opening]);

  useEffect(() => {
    update(tag, "pv", Number(pv.toFixed(2)));
  }, [pv]);

  // =========================
  // AUTO / CASCADE CONTROL
  // =========================
  useEffect(() => {

    if (data.mode === "Manual") return;

    const interval = setInterval(() => {

      const sp = Number(data.sp ?? 0);
      const pvVal = pv;
      const opening = Number(data.opening ?? 0);

      const error = sp - pvVal;

      const Kp = 1.2;

      let targetOpening =
        (sp / MAX_FLOW) * 100 + (error * Kp);

      targetOpening =
        (targetOpening * 0.7) + (opening * 0.3);

      targetOpening = Math.max(0, Math.min(100, targetOpening));

      const step = 0.6;

      let newOpening = opening;

      if (Math.abs(targetOpening - opening) > step) {
        newOpening =
          opening + Math.sign(targetOpening - opening) * step;
      } else {
        newOpening = targetOpening;
      }

      update(tag, "opening", Number(newOpening.toFixed(2)));

    }, 200);

    return () => clearInterval(interval);

  }, [data.mode, data.sp, data.opening, pv, tag]);

  // =========================
  // RENDER (RESTORED UI)
  // =========================
  return (
    <div
      className="faceplate"
      style={{
        position: "absolute", 
        left: position.x,
        top: position.y
      }}
    >

      {/* HEADER */}
      <h3
        className="header"
        onMouseDown={handleMouseDown}
        style={{ cursor: "move" }}
      >
        {tag}
      </h3>

      {/* MODE BUTTONS */}
      <div className="mode-buttons">
        {["Manual", "Auto", "Cascade"].map((m) => (
          <button
            key={m}
            className={`mode-btn ${data.mode === m ? "active" : ""}`}
            onClick={() => update(tag, "mode", m)}
          >
            {m}
          </button>
        ))}
      </div>

      {/* OPENING */}
      <div className="row">
        <label>Opening %</label>
        <input
          type="number"
          value={data.opening}
          onChange={(e) =>
            update(tag, "opening", Number(e.target.value))
          }
        />
      </div>

      {/* INCREMENT / DECREMENT */}
      <div className="increment-buttons">
        <button onClick={() =>
          update(tag, "opening", Math.min(data.opening + 0.5, 100))
        }>▲</button>

        <button onClick={() =>
          update(tag, "opening", Math.max(data.opening - 0.5, 0))
        }>▼</button>
      </div>

      {/* SP */}
      <div className="sp-button">
        <label>SP</label>
        <input
          value={data.sp}
          onChange={(e) =>
            update(tag, "sp", Number(e.target.value))
          }
        />
      </div>

      {/* PV */}
      <div className="pv-button">
        <label>PV</label>
        <input value={pv.toFixed(2)} readOnly />
      </div>

      {/* CLOSE */}
      <button className="close-btn" onClick={close}>
        Close
      </button>

    </div>
  );
}

export default ControlValveControlPanel;